import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartIcon, BrainIcon, SparklesIcon, SunIcon, CalendarIcon, Users, ArrowRightIcon, MenuIcon, XIcon } from 'lucide-react';
const HERO_MOUNTAIN_IMAGE = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80';
export function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 30
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    },
    transition: {
      duration: 0.65,
      ease: 'easeOut'
    }
  };
  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1
      }
    },
    viewport: {
      once: true,
      margin: '-80px'
    }
  };
  const accentText = 'DFB Metodika:';
  const charVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.045,
        duration: 0.4,
        ease: 'easeOut'
      }
    })
  };
  const headlineVariants = {
    hidden: {
      opacity: 0,
      y: 24,
      filter: 'blur(12px)'
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.9,
        ease: 'easeOut',
        delay: 0.3
      }
    }
  };
  const subVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: 'easeOut',
        delay: 0.9
      }
    }
  };
  const ctaVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        delay: 1.4
      }
    }
  };
  // Reusable section label
  const SectionLabel = ({
    text,
    light = false
  }: {
    text: string;
    light?: boolean;
  }) => <div className="flex flex-col items-center mb-5" data-id="element-534">
      <p className={`text-xs uppercase tracking-widest font-semibold mb-3 ${light ? 'text-gold-light/70' : 'text-gold-dark'}`} data-id="element-535">
        {text}
      </p>
      <div className="w-10 h-0.5 bg-gold" data-id="element-536" />
    </div>;
  return <main className="w-full bg-sand-lightest" data-id="element-537">
      {/* ── STICKY NAV ── */}
      <motion.nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-500" style={{
      background: scrolled ? 'rgba(var(--mountain-darker), 0.96)' : 'transparent',
      backdropFilter: scrolled ? 'blur(12px)' : 'none',
      boxShadow: scrolled ? '0 1px 0 rgba(255,255,255,0.06)' : 'none'
    }} initial={{
      y: -80,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.6,
      ease: 'easeOut',
      delay: 2.0
    }} data-id="element-538">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-539">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 select-none" data-id="element-540">
            <span className="text-gold-light font-bold text-lg tracking-wide" data-id="element-541">
              DFB
            </span>
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8" data-id="element-542">
            {[{
            label: 'Apie',
            href: '#apie'
          }, {
            label: 'Metodika',
            href: '#metodika'
          }, {
            label: 'Programa',
            href: '#programa'
          }, {
            label: 'Komanda',
            href: '#komanda'
          }, {
            label: 'Tvarkaraštis',
            href: '#tvarkarastis'
          }].map(link => <a key={link.label} href={link.href} className="text-white/70 hover:text-white text-sm font-medium transition-colors duration-200" data-id="element-543">
                {link.label}
              </a>)}
          </div>

          {/* CTA */}
          <div className="hidden md:block" data-id="element-544">
            <motion.a href="#registracija" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} className="bg-gold hover:bg-gold-dark text-mountain-darker px-6 py-2.5 rounded-full text-sm font-semibold transition-colors shadow-lg" data-id="element-545">
              Registruotis
            </motion.a>
          </div>

          {/* Mobile burger */}
          <button className="md:hidden text-white/80 hover:text-white p-1" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Meniu" data-id="element-546">
            {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-547" /> : <MenuIcon className="w-6 h-6" data-id="element-548" />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence data-id="element-549">
          {mobileOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} className="md:hidden overflow-hidden" style={{
          background: 'rgba(var(--mountain-darker), 0.98)'
        }} data-id="element-550">
              <div className="px-6 py-4 flex flex-col gap-4 border-t border-white/10" data-id="element-551">
                {[{
              label: 'Apie',
              href: '#apie'
            }, {
              label: 'Metodika',
              href: '#metodika'
            }, {
              label: 'Programa',
              href: '#programa'
            }, {
              label: 'Komanda',
              href: '#komanda'
            }, {
              label: 'Tvarkaraštis',
              href: '#tvarkarastis'
            }].map(link => <a key={link.label} href={link.href} onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white text-base font-medium py-1 transition-colors" data-id="element-552">
                    {link.label}
                  </a>)}
                <a href="#registracija" onClick={() => setMobileOpen(false)} className="bg-gold text-mountain-darker px-6 py-3 rounded-full text-sm font-semibold text-center mt-2" data-id="element-553">
                  Registruotis
                </a>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.nav>

      {/* ── HERO ── */}
      <section className="relative h-screen min-h-[640px] flex flex-col items-center justify-center px-4 overflow-hidden" data-id="element-554">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url('${HERO_MOUNTAIN_IMAGE}')`
      }} data-id="element-555" />
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 45%, transparent 20%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.88) 100%)'
      }} data-id="element-556" />

        <div className="relative z-20 max-w-4xl mx-auto text-center flex flex-col items-center" data-id="element-557">
          <motion.p className="text-sm font-accent text-gold-light mb-5 tracking-widest uppercase" initial="hidden" animate="visible" aria-label={accentText} data-id="element-558">
            {accentText.split('').filter((_, i) => i !== 7).map((char, i) => <motion.span key={i} custom={i} variants={charVariants} style={{
            display: char === ' ' ? 'inline' : 'inline-block'
          }} data-id="element-559">
                  {char === ' ' ? '\u00A0' : char}
                </motion.span>)}
          </motion.p>

          <motion.h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight" variants={headlineVariants} initial="hidden" animate="visible" data-id="element-560">
            Atrask savo <span className="text-gold-light" data-id="element-561">pusiausvyrą</span>
            <br data-id="element-562" />
            nuolatinės kaitos pasaulyje
          </motion.h1>

          <motion.p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed" variants={subVariants} initial="hidden" animate="visible" data-id="element-563">
            Išmok valdyti savo psichines būsenas ir gyventi darniai su savimi ir
            aplinka.
          </motion.p>

          <motion.button variants={ctaVariants} initial="hidden" animate="visible" whileHover={{
          scale: 1.05,
          boxShadow: '0 20px 60px -10px rgba(0,0,0,0.4)'
        }} whileTap={{
          scale: 0.97
        }} className="bg-white hover:bg-gray-100 text-gray-900 px-14 py-5 rounded-full text-lg font-semibold shadow-2xl transition-colors" data-id="element-564">
            Sužinok Daugiau
          </motion.button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-30" data-id="element-565">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" data-id="element-566">
            <path d="M0 0C240 80 480 80 720 40C960 0 1200 0 1440 40V120H0V0Z" style={{
            fill: 'rgb(var(--sand-light))'
          }} data-id="element-567" />
          </svg>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section id="apie" className="py-20 px-4 bg-sand-light" data-id="element-568">
        <motion.div className="max-w-3xl mx-auto text-center" {...fadeInUp} data-id="element-569">
          <p className="text-lg md:text-xl text-mountain-dark leading-relaxed" data-id="element-570">
            Šis kursas pakeis jūsų požiūrį į meditaciją, suteikdamas praktinių
            įrankių ir technikų, kaip atpažinti, suprasti ir veiksmingai valdyti
            savo vidines būsenas, siekiant geresnės gyvenimo kokybės ir
            harmonijos.
          </p>
        </motion.div>
      </section>

      {/* ── CHALLENGES ── */}
      <section className="py-24 px-4 bg-sand-lightest" data-id="element-571">
        <div className="max-w-6xl mx-auto" data-id="element-572">
          <motion.div className="mb-12" {...fadeInUp} data-id="element-573">
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker mb-4" data-id="element-574">
              Kasdieniai Iššūkiai,
            </h2>
            <div className="w-16 h-px bg-gold mb-4" data-id="element-575" />
            <p className="font-accent italic text-2xl md:text-3xl text-gold-dark" data-id="element-576">
              Kuriuos Visi Pažįstame
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-4" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-577">
            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-578">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80')"
            }} data-id="element-579" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-580" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-581">
                01
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-582">
                <p className="text-lg font-bold text-white mb-1" data-id="element-583">
                  Nuolatinis stresas
                </p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-584">
                  Kaip efektyviai spręsti problemas neprarandant vidinės
                  ramybės?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-585">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600&q=80')"
            }} data-id="element-586" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-587" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-588">
                02
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-589">
                <p className="text-lg font-bold text-white mb-1" data-id="element-590">
                  Dėmesio blaškymasis
                </p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-591">
                  Kaip valdyti dėmesį nuolat besikeičiančioje aplinkoje?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-592">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1545389336-cf090694435e?w=600&q=80')"
            }} data-id="element-593" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-594" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-595">
                03
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-596">
                <p className="text-lg font-bold text-white mb-1" data-id="element-597">Perdegimas</p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-598">
                  Kaip siekti ilgalaikių tikslų neperdegant?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-599">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1448375240586-882707db888b?w=600&q=80')"
            }} data-id="element-600" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-601" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-602">
                04
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-603">
                <p className="text-lg font-bold text-white mb-1" data-id="element-604">Chaosas</p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-605">
                  Ką daryti su emocijomis sudėtingose situacijose?
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── TARP NUOLATINĖS KAITOS ── */}
      <section className="py-24 px-4 bg-mountain-darker text-white" data-id="element-606">
        <div className="max-w-6xl mx-auto" data-id="element-607">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-608">
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4" data-id="element-609">
              Tarp nuolatinės kaitos ir
            </h2>
            <div className="w-16 h-px bg-gold mx-auto mb-4" data-id="element-610" />
            <p className="font-accent italic text-2xl md:text-3xl text-gold-light" data-id="element-611">
              pusiausvyros paieškų
            </p>
          </motion.div>

          <motion.div className="flex flex-col gap-12 items-center" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-612">
            <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto" data-id="element-613">
              <p className="text-xl md:text-2xl text-white/80 leading-relaxed" data-id="element-614">
                Šiandienos pasaulis sukasi neregėtu tempu. Informacijos srautas,
                nuolatiniai reikalavimai ir pokyčiai kuria vidinę įtampą, kuri
                laikui bėgant virsta stresu, perdegimo jausmu ir prarastu ryšiu
                su savimi.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="w-full max-w-2xl mx-auto space-y-0" data-id="element-615">
              {['Kaip efektyviai spręsti problemas neprarandant vidinės ramybės?', 'Kaip giliai atsipalaiduoti ir tikrai pailsėti?', 'Kaip pasiekti gyvenime harmoniją ir pilnatvę?', 'Kaip valdyti emocijas ir dėmesį sudėtingose situacijose?'].map((question, i) => <div key={i} className="flex items-start gap-5 py-6" style={{
              borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.1)' : 'none'
            }} data-id="element-616">
                  <span className="font-bold text-gold-light flex-shrink-0" style={{
                fontSize: '2rem',
                lineHeight: 1.1
              }} data-id="element-617">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-white/80 text-lg leading-relaxed pt-0.5" data-id="element-618">
                    {question}
                  </p>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── WHAT IS DFB ── */}
      <section id="metodika" className="relative overflow-hidden" style={{
      minHeight: 600
    }} data-id="element-619">
        {/* Full-width background image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1920&q=80')"
      }} data-id="element-620" />
        {/* Left-to-right cinematic gradient */}
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(to right, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.72) 40%, rgba(0,0,0,0.25) 70%, transparent 100%)'
      }} data-id="element-621" />

        {/* Content — left half */}
        <motion.div className="relative z-10 flex items-center" style={{
        minHeight: 600
      }} {...fadeInUp} data-id="element-622">
          <div className="max-w-7xl mx-auto w-full px-8 md:px-16 py-24" data-id="element-623">
            <div className="max-w-xl" data-id="element-624">
              {/* Section heading */}
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight" data-id="element-625">
                Sąmoningas Būsenų Valdymas
              </h2>
              {/* Gold line */}
              <div className="w-16 h-px bg-gold mb-4" data-id="element-626" />
              {/* Italic subtitle */}
              <p className="font-accent italic text-2xl md:text-3xl text-gold-light mb-10" data-id="element-627">
                Kaip Sprendimas
              </p>

              {/* DFB label */}
              <p className="text-xs uppercase tracking-widest font-semibold text-gold mb-4" data-id="element-628">
                Kas Yra DFB?
              </p>

              {/* Paragraphs */}
              <p className="text-lg text-white/80 leading-relaxed mb-5" data-id="element-629">
                Diferencijuotos Funkcinės Būsenos – tai aktyvios
                savireguliacijos metodika, leidžianti sąmoningai valdyti vidines
                būsenas nepriklausomai nuo išorinės veiklos.
              </p>
              <p className="text-lg text-white/80 leading-relaxed" data-id="element-630">
                Metodika unikali tuo, kad moko ne slopinti emocijas, o jas
                diferencijuoti, sąmoningai pasirinkti ir pritaikyti.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── 16 STATES ── */}
      <section className="py-24 px-4 bg-mountain-darker text-white" data-id="element-631">
        <div className="max-w-6xl mx-auto" data-id="element-632">
          <motion.div className="text-center mb-12" {...fadeInUp} data-id="element-633">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-634">
              16 Būsenų
            </h3>
            <div className="w-16 h-px bg-gold mx-auto mb-4" data-id="element-635" />
            <p className="font-accent italic text-2xl md:text-3xl text-gold-light" data-id="element-636">
              Aktyvios Savireguliacijos Sistema
            </p>
          </motion.div>

          <motion.div className="mb-16 max-w-3xl mx-auto" {...fadeInUp} data-id="element-637">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-2xl text-center" data-id="element-638">
              <p className="text-white/80 leading-relaxed mb-4" data-id="element-639">
                DFB metodika dalinasi į žvalgą: kiekvienai veiklai reikalinga
                skirtinga psichikos būsena.
              </p>
              <p className="text-white/80 leading-relaxed mb-4" data-id="element-640">
                Trumpų distancijų bėgimas reikalauja kitokio pasirengimo nei
                maratonas.
              </p>
              <p className="text-white/80 leading-relaxed" data-id="element-641">
                Lengvas pokalbis - tai viena būsena, o intensyvus protinis
                darbas - visai kita.
              </p>
            </div>
          </motion.div>

          {/* 4-Column Horizontal Flow */}
          <motion.div className="relative max-w-5xl mx-auto" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-642">
            {/* Horizontal connecting line — sits at circle center (top-10 = 40px = half of w-20/h-20) */}
            <div className="absolute hidden md:block h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" style={{
            top: '40px',
            left: '12.5%',
            right: '12.5%'
          }} data-id="element-643" />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6" data-id="element-644">
              {[{
              title: '4 Mentalinio Aktyvumo Būsenos',
              desc: 'Nuo chaoso iki tikslaus mąstymo',
              img: 'https://images.unsplash.com/photo-1617791160588-241658c0f566?w=200&q=80'
            }, {
              title: '4 Emocinio Aktyvumo Būsenos',
              desc: 'Nuo gilumo ir jautrumo iki lengvumo ir dinamiškumo bendraujant',
              img: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=200&q=80'
            }, {
              title: '4 Fizinio Aktyvumo Būsenos',
              desc: 'Nuo plaučių, erdivinių judėsių iki tikslingo, koordinuotos veiklos',
              img: 'https://images.unsplash.com/photo-1545389336-cf090694435e?w=200&q=80'
            }, {
              title: '4 Harmoningo Aktyvumo Būsenos',
              desc: 'Kūnas, emocijos ir protas veikia darniai kaip viena visuma',
              img: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=200&q=80'
            }].map((item, idx) => <motion.div key={idx} variants={fadeInUp} className="flex flex-col items-center" data-id="element-645">
                  {/* Circular image */}
                  <div className="relative z-10 w-20 h-20 rounded-full overflow-hidden border-4 border-gold ring-2 ring-gold/25 flex-shrink-0" data-id="element-646">
                    <div className="w-full h-full bg-cover bg-center" style={{
                  backgroundImage: `url('${item.img}')`
                }} data-id="element-647" />
                  </div>
                  {/* Vertical connector */}
                  <div className="w-px h-8 bg-gradient-to-b from-gold/60 to-gold/20 mt-0" data-id="element-648" />
                  {/* Title */}
                  <h4 className="text-base font-bold text-white text-center mt-2 leading-snug" data-id="element-649">
                    {item.title}
                  </h4>
                  {/* Description */}
                  <p className="text-sm text-white/55 text-center mt-2 leading-relaxed max-w-[160px]" data-id="element-650">
                    {item.desc}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="py-24 px-4 bg-sand-lightest" data-id="element-651">
        <div className="max-w-5xl mx-auto" data-id="element-652">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-653">
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker mb-4" data-id="element-654">
              DFB Metodologijos Nauda
            </h2>
            <div className="w-16 h-px bg-gold mx-auto mb-4" data-id="element-655" />
            <p className="font-accent italic text-2xl md:text-3xl text-gold-dark" data-id="element-656">
              ir Rezultatai
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-657">
            {/* First row: 3 items */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 mb-12" data-id="element-658">
              {[{
              icon: <HeartIcon className="w-12 h-12 text-gold-dark" data-id="element-659" />,
              title: 'Nuolatinė Ramybė',
              desc: 'Stabilumas nepriklausomai nuo išorinio pasaulio'
            }, {
              icon: <BrainIcon className="w-12 h-12 text-gold-dark" data-id="element-660" />,
              title: 'Dėmesio Valdymas',
              desc: 'Sąmoningas dėmesio nukreipimas į svarbius dalykus'
            }, {
              icon: <SparklesIcon className="w-12 h-12 text-gold-dark" data-id="element-661" />,
              title: 'Aktyvi Savireguliacija',
              desc: 'Efektyvus streso ir emocijų valdymas'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-662">
                  {benefit.icon}
                  <div className="w-8 h-px bg-gold mx-auto my-4" data-id="element-663" />
                  <h3 className="text-xl font-bold text-mountain-darker mb-2" data-id="element-664">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-mountain-dark/60 max-w-[180px] mx-auto leading-relaxed" data-id="element-665">
                    {benefit.desc}
                  </p>
                </motion.div>)}
            </div>

            {/* Second row: 2 items centered */}
            <div className="flex flex-col sm:flex-row justify-center gap-12" data-id="element-666">
              {[{
              icon: <Users className="w-12 h-12 text-gold-dark" data-id="element-667" />,
              title: 'Emocinis Intelektas',
              desc: 'Geresnis savęs ir kitų supratimas'
            }, {
              icon: <SunIcon className="w-12 h-12 text-gold-dark" data-id="element-668" />,
              title: 'Giluminis Poilsis',
              desc: 'Efektyvus atsigavimas per trumpą laiką'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center text-center sm:w-[calc(33.333%-1.5rem)]" data-id="element-669">
                  {benefit.icon}
                  <div className="w-8 h-px bg-gold mx-auto my-4" data-id="element-670" />
                  <h3 className="text-xl font-bold text-mountain-darker mb-2" data-id="element-671">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-mountain-dark/60 max-w-[180px] mx-auto leading-relaxed" data-id="element-672">
                    {benefit.desc}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── COURSE OFFER ── */}
      <section id="registracija" className="py-24 px-4 bg-mountain-darker text-white" data-id="element-673">
        <div className="max-w-5xl mx-auto" data-id="element-674">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-675">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-676">
              Prisijunk Prie Kurso
            </h2>
            <div className="w-16 h-px bg-gold mx-auto mb-4" data-id="element-677" />
            <p className="font-accent italic text-2xl md:text-3xl text-gold-light" data-id="element-678">
              DFB Metodika: Sąmoninga Savireguliacija
            </p>
          </motion.div>

          <motion.div className="flex flex-col sm:flex-row items-stretch justify-center mb-12" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-679">
            {[{
            value: '90',
            label: 'Akademinių valandų',
            sub: '30 užsiėmimų po 2–2.5 val.'
          }, {
            value: '2.5',
            label: 'Mėnesiai',
            sub: 'Programos trukmė'
          }, {
            value: '10',
            label: 'Dalyvių max',
            sub: 'Optimali grupė'
          }, {
            value: '16',
            label: 'Būsenų sistema',
            sub: 'Sertifikuotas instruktorius'
          }].map((stat, i, arr) => <motion.div key={stat.value} variants={fadeInUp} className="flex-1 flex flex-col items-center text-center px-8 py-8 relative" data-id="element-680">
                {i < arr.length - 1 && <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2" style={{
              width: 1,
              height: '60%',
              background: 'rgba(255,255,255,0.12)'
            }} data-id="element-681" />}
                <span className="font-bold text-gold-light leading-none mb-3" style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)'
            }} data-id="element-682">
                  {stat.value}
                </span>
                <p className="text-sm font-semibold text-white uppercase tracking-wide mb-1" data-id="element-683">
                  {stat.label}
                </p>
                <p className="text-xs text-white/50 leading-relaxed" data-id="element-684">
                  {stat.sub}
                </p>
              </motion.div>)}
          </motion.div>

          <div className="w-full h-px mb-12" style={{
          background: 'rgba(255,255,255,0.1)'
        }} data-id="element-685" />

          <motion.div className="text-center" {...fadeInUp} data-id="element-686">
            <motion.button whileHover={{
            backgroundColor: 'rgb(var(--gold-dark))',
            scale: 1.03
          }} whileTap={{
            scale: 0.97
          }} className="bg-gold text-mountain-darker px-14 py-5 rounded-full text-lg font-semibold shadow-lg transition-colors mb-5" data-id="element-687">
              Registruotis į Kursą
            </motion.button>
            <div className="flex items-center justify-center gap-2 mt-3" data-id="element-688">
              <span className="inline-block w-2 h-2 rounded-full flex-shrink-0 bg-red-400" data-id="element-689" />
              <p className="text-sm text-white/50" data-id="element-690">
                Vietos ribotos – užsiregistruok jau šiandien
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── COURSE STRUCTURE ── */}
      <section id="programa" className="py-24 px-4 bg-sand-light" data-id="element-691">
        <div className="max-w-6xl mx-auto" data-id="element-692">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-693">
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker mb-4" data-id="element-694">
              Kurso Struktūra
            </h2>
            <div className="w-16 h-px bg-gold mx-auto mb-4" data-id="element-695" />
            <p className="font-accent italic text-2xl md:text-3xl text-gold-dark" data-id="element-696">
              Trys žingsniai į harmoniją
            </p>
          </motion.div>

          <motion.div className="space-y-4" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-697">
            {[{
            num: '01',
            title: 'Pirmoji Dalis: Pagrindai',
            desc: 'Baziniai pratimai ir teoriniai pagrindai',
            img: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=1200&q=80'
          }, {
            num: '02',
            title: 'Antroji Dalis: Būsenos',
            desc: '16 aktyvumo būsenų atpažinimas ir valdymas',
            img: 'https://images.unsplash.com/photo-1545389336-cf090694435e?w=1200&q=80'
          }, {
            num: '03',
            title: 'Trečioji Dalis: Integracija',
            desc: 'Įgūdžių taikymas kasdieniame gyvenime',
            img: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=1200&q=80'
          }].map(part => <motion.div key={part.num} variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 200
          }} data-id="element-698">
                {/* Background image */}
                <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: `url('${part.img}')`
            }} data-id="element-699" />
                {/* Left-to-right dark gradient */}
                <div className="absolute inset-0" style={{
              background: 'linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.1) 100%)'
            }} data-id="element-700" />
                {/* Ghost number — far right */}
                <div className="absolute right-8 top-1/2 -translate-y-1/2 font-bold select-none leading-none text-white" style={{
              fontSize: '8rem',
              opacity: 0.1
            }} aria-hidden="true" data-id="element-701">
                  {part.num}
                </div>
                {/* Content — left side */}
                <div className="relative z-10 flex items-center gap-6 px-10 py-10" data-id="element-702">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/60 flex items-center justify-center" data-id="element-703">
                    <span className="text-gold-light font-bold text-sm tracking-widest" data-id="element-704">
                      {part.num}
                    </span>
                  </div>
                  <div data-id="element-705">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-1 leading-tight" data-id="element-706">
                      {part.title}
                    </h3>
                    <p className="text-white/70 text-base md:text-lg leading-relaxed" data-id="element-707">
                      {part.desc}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-24 px-4 bg-sand-lightest overflow-hidden" data-id="element-708">
        <div className="max-w-4xl mx-auto" data-id="element-709">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-710">
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker mb-4" data-id="element-711">
              Dalyvių Atsiliepimai
            </h2>
            <div className="w-16 h-px bg-gold mx-auto mb-4" data-id="element-712" />
            <p className="font-accent italic text-2xl md:text-3xl text-gold-dark" data-id="element-713">
              Tikros transformacijos istorijos
            </p>
          </motion.div>

          {(() => {
          const testimonials = [{
            name: 'Gabrielė J.',
            role: 'Kursų dalyvė',
            img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80',
            quote: 'Mokymai padėjo geriau suprasti savo reakcijas ir rasti ramybę. Tai buvo tikras lūžio taškas mano gyvenime – dabar jaučiuosi stipresnė ir labiau susitelkusi.'
          }, {
            name: 'Jonas P.',
            role: 'Kursų dalyvis',
            img: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&q=80',
            quote: 'Dėmesio koncentracija išaugo, darbas tapo efektyvesnis. DFB metodika suteikė man konkrečius įrankius, kuriuos naudoju kasdien.'
          }, {
            name: 'Laura K.',
            role: 'Kursų dalyvė',
            img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80',
            quote: 'Emocinė stiprybė ir aiškumas padėjo įveikti gyvenimo iššūkius. Rekomenduoju kiekvienam, kuris nori gyventi sąmoningiau ir harmoningiau.'
          }, {
            name: 'Tomas N.',
            role: 'Kursų dalyvis',
            img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80',
            quote: 'Pagerėjo bendravimas, aiškiau išreiškiu mintis. Kursas viršijo visus lūkesčius – tai ne tik teorija, bet ir praktika, kuri iš tikrųjų veikia.'
          }];
          const [activeIdx, setActiveIdx] = useState(0);
          const [direction, setDirection] = useState(0);
          const goTo = (idx: number) => {
            setDirection(idx > activeIdx ? 1 : -1);
            setActiveIdx(idx);
          };
          const prev = () => goTo(activeIdx === 0 ? testimonials.length - 1 : activeIdx - 1);
          const next = () => goTo(activeIdx === testimonials.length - 1 ? 0 : activeIdx + 1);
          const t = testimonials[activeIdx];
          const slideVariants = {
            enter: (dir: number) => ({
              opacity: 0,
              x: dir * 60
            }),
            center: {
              opacity: 1,
              x: 0
            },
            exit: (dir: number) => ({
              opacity: 0,
              x: dir * -60
            })
          };
          return <div className="relative flex flex-col items-center" data-id="element-714">
                <div className="flex items-center w-full gap-4 md:gap-8" data-id="element-715">
                  <button onClick={prev} aria-label="Ankstesnis" className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-gold/40 bg-white hover:bg-gold hover:border-gold text-gold-dark hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm" data-id="element-716">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-717">
                      <polyline points="15 18 9 12 15 6" data-id="element-718" />
                    </svg>
                  </button>

                  <div className="flex-1 min-w-0 overflow-hidden" data-id="element-719">
                    <AnimatePresence mode="wait" custom={direction} data-id="element-720">
                      <motion.div key={activeIdx} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                    duration: 0.45,
                    ease: 'easeInOut'
                  }} className="flex flex-col items-center text-center px-2 md:px-8" data-id="element-721">
                        <span className="font-serif text-gold/30 select-none leading-none mb-2" style={{
                      fontSize: '8rem',
                      lineHeight: 0.8
                    }} aria-hidden="true" data-id="element-722">
                          "
                        </span>
                        <p className="font-accent italic text-mountain-darker leading-relaxed mb-10" style={{
                      fontSize: 'clamp(1.35rem, 3vw, 2rem)'
                    }} data-id="element-723">
                          {t.quote}
                        </p>
                        <div className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-gold ring-offset-4 ring-offset-sand-lightest mb-4 flex-shrink-0" data-id="element-724">
                          <div className="w-full h-full bg-cover bg-center" style={{
                        backgroundImage: `url('${t.img}')`
                      }} data-id="element-725" />
                        </div>
                        <p className="font-bold text-mountain-darker text-lg" data-id="element-726">
                          {t.name}
                        </p>
                        <p className="text-sm text-mountain-dark/60 mt-0.5" data-id="element-727">
                          {t.role}
                        </p>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  <button onClick={next} aria-label="Kitas" className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-gold/40 bg-white hover:bg-gold hover:border-gold text-gold-dark hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm" data-id="element-728">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-729">
                      <polyline points="9 18 15 12 9 6" data-id="element-730" />
                    </svg>
                  </button>
                </div>

                <div className="flex items-center gap-3 mt-10" data-id="element-731">
                  {testimonials.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Atsiliepimas ${i + 1}`} className="transition-all duration-300 rounded-full" style={{
                width: i === activeIdx ? '2rem' : '0.5rem',
                height: '0.5rem',
                background: i === activeIdx ? 'rgb(var(--gold))' : 'rgba(var(--gold), 0.3)'
              }} data-id="element-732" />)}
                </div>
              </div>;
        })()}
        </div>
      </section>

      {/* ── TEAM ── */}
      <section id="komanda" className="py-32 px-4 bg-mountain-darker text-white" data-id="element-733">
        <div className="max-w-4xl mx-auto" data-id="element-734">
          <motion.div className="text-center mb-20" {...fadeInUp} data-id="element-735">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-736">
              Mūsų Komanda
            </h2>
            <div className="w-16 h-px bg-gold mx-auto mb-4" data-id="element-737" />
            <p className="font-accent italic text-2xl md:text-3xl text-gold-light" data-id="element-738">
              Patyrę instruktoriai ir praktikai
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-739">
            <motion.div variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-740">
              <div className="relative w-52 h-52 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold ring-offset-8 ring-offset-mountain-darker flex-shrink-0" data-id="element-741">
                <div className="absolute inset-0 bg-cover bg-center" style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80')"
              }} data-id="element-742" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-743">
                Agnė Kubertė
              </h3>
              <p className="text-sm uppercase tracking-widest text-gold-light font-semibold mb-6" data-id="element-744">
                DFB metodologijos Instruktorė
              </p>
              <div className="w-12 h-px bg-gold/40 mb-6" data-id="element-745" />
              <p className="text-white/70 leading-relaxed mb-8 max-w-xs" data-id="element-746">
                Daugiau nei 10 metų patirtis savireguliacijos ir mindfulness
                srityje. Veda grupinius ir individualius užsiėmimus, padedančius
                žmonėms atrasti vidinę pusiausvyrą.
              </p>
              <div className="relative max-w-xs" data-id="element-747">
                <span className="absolute -top-4 -left-2 font-serif text-gold/30 select-none leading-none" style={{
                fontSize: '4rem',
                lineHeight: 1
              }} aria-hidden="true" data-id="element-748">
                  "
                </span>
                <p className="font-accent italic text-white/75 text-lg leading-relaxed pl-4" data-id="element-749">
                  Kiekvienas žmogus turi savyje resursų – mano darbas padėti
                  juos atrasti.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-750">
              <div className="relative w-52 h-52 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold ring-offset-8 ring-offset-mountain-darker flex-shrink-0" data-id="element-751">
                <div className="absolute inset-0 bg-cover bg-center" style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80')"
              }} data-id="element-752" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-753">
                Vestina Zablockyė
              </h3>
              <p className="text-sm uppercase tracking-widest text-gold-light font-semibold mb-6" data-id="element-754">
                DFB metodologijos Instruktorė
              </p>
              <div className="w-12 h-px bg-gold/40 mb-6" data-id="element-755" />
              <p className="text-white/70 leading-relaxed mb-8 max-w-xs" data-id="element-756">
                Specializuojasi emocinio intelekto ugdyme ir streso valdyme. Jos
                praktinis požiūris padeda dalyviams greitai integruoti DFB
                technikas į kasdienį gyvenimą.
              </p>
              <div className="relative max-w-xs" data-id="element-757">
                <span className="absolute -top-4 -left-2 font-serif text-gold/30 select-none leading-none" style={{
                fontSize: '4rem',
                lineHeight: 1
              }} aria-hidden="true" data-id="element-758">
                  "
                </span>
                <p className="font-accent italic text-white/75 text-lg leading-relaxed pl-4" data-id="element-759">
                  Sąmoningumas – tai ne tikslas, o kelias, kuriuo einame
                  kiekvieną dieną.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SCHEDULE ── */}
      <section id="tvarkarastis" className="py-24 px-4 bg-sand-lightest" data-id="element-760">
        <div className="max-w-6xl mx-auto" data-id="element-761">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-762">
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker mb-4" data-id="element-763">
              Artėjantis Kursas
            </h2>
            <div className="w-16 h-px bg-gold mx-auto mb-4" data-id="element-764" />
            <p className="font-accent italic text-2xl md:text-3xl text-gold-dark" data-id="element-765">
              Prisijunk prie pavasario grupės
            </p>
          </motion.div>

          <motion.div {...fadeInUp} className="space-y-10" data-id="element-766">
            {/* Date + days — unified card */}
            <div className="bg-white border border-sand-dark/20 rounded-2xl px-8 py-6 max-w-2xl mx-auto" data-id="element-767">
              <div className="flex items-center gap-3 justify-center mb-5" data-id="element-768">
                <CalendarIcon className="w-5 h-5 text-gold-dark flex-shrink-0" data-id="element-769" />
                <p className="text-2xl font-bold text-mountain-darker" data-id="element-770">
                  Kovo 16 – Gegužės 21
                </p>
              </div>
              <div className="w-12 h-px bg-gold mx-auto mb-5" data-id="element-771" />
              <div className="flex flex-wrap justify-center gap-2" data-id="element-772">
                {['Pirmadienis', 'Antradienis', 'Ketvirtadienis'].map(day => <div key={day} className="flex items-center gap-2 bg-sand-lightest border border-sand-dark/20 px-4 py-2 rounded-xl" data-id="element-773">
                    <div className="w-1.5 h-1.5 bg-gold rounded-full" data-id="element-774" />
                    <span className="text-mountain-darker font-medium text-sm" data-id="element-775">
                      {day}
                    </span>
                  </div>)}
              </div>
            </div>

            <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
            once: true,
            margin: '-80px'
          }} data-id="element-776">
              <motion.div variants={fadeInUp} className="bg-white border border-sand-dark/30 p-5 rounded-2xl" data-id="element-777">
                <h3 className="text-base font-bold text-mountain-darker mb-4 text-center tracking-wide" data-id="element-778">
                  Kovas 2026
                </h3>
                <div className="grid grid-cols-7 gap-1" data-id="element-779">
                  {['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'].map(d => <div key={d} className="text-center text-[10px] text-mountain-dark/40 font-semibold pb-1" data-id="element-780">
                      {d}
                    </div>)}
                  {[...Array(6)].map((_, i) => <div key={`e-${i}`} className="aspect-square" data-id="element-781" />)}
                  {[...Array(31)].map((_, i) => {
                  const day = i + 1;
                  const dow = (i + 6) % 7;
                  const active = (dow === 0 || dow === 1 || dow === 3) && day >= 16;
                  return <div key={day} className={`aspect-square flex items-center justify-center rounded text-[11px] font-medium ${active ? 'bg-gold text-white font-bold' : 'text-mountain-dark/50'}`} data-id="element-782">
                        {day}
                      </div>;
                })}
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="bg-white border border-sand-dark/30 p-5 rounded-2xl" data-id="element-783">
                <h3 className="text-base font-bold text-mountain-darker mb-4 text-center tracking-wide" data-id="element-784">
                  Balandis 2026
                </h3>
                <div className="grid grid-cols-7 gap-1" data-id="element-785">
                  {['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'].map(d => <div key={d} className="text-center text-[10px] text-mountain-dark/40 font-semibold pb-1" data-id="element-786">
                      {d}
                    </div>)}
                  {[...Array(2)].map((_, i) => <div key={`e-${i}`} className="aspect-square" data-id="element-787" />)}
                  {[...Array(30)].map((_, i) => {
                  const day = i + 1;
                  const dow = (i + 2) % 7;
                  const active = dow === 0 || dow === 1 || dow === 3;
                  return <div key={day} className={`aspect-square flex items-center justify-center rounded text-[11px] font-medium ${active ? 'bg-gold text-white font-bold' : 'text-mountain-dark/50'}`} data-id="element-788">
                        {day}
                      </div>;
                })}
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="bg-white border border-sand-dark/30 p-5 rounded-2xl" data-id="element-789">
                <h3 className="text-base font-bold text-mountain-darker mb-4 text-center tracking-wide" data-id="element-790">
                  Gegužė 2026
                </h3>
                <div className="grid grid-cols-7 gap-1" data-id="element-791">
                  {['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'].map(d => <div key={d} className="text-center text-[10px] text-mountain-dark/40 font-semibold pb-1" data-id="element-792">
                      {d}
                    </div>)}
                  {[...Array(4)].map((_, i) => <div key={`e-${i}`} className="aspect-square" data-id="element-793" />)}
                  {[...Array(31)].map((_, i) => {
                  const day = i + 1;
                  const dow = (i + 4) % 7;
                  const active = (dow === 0 || dow === 1 || dow === 3) && day <= 21;
                  return <div key={day} className={`aspect-square flex items-center justify-center rounded text-[11px] font-medium ${active ? 'bg-gold text-white font-bold' : 'text-mountain-dark/50'}`} data-id="element-794">
                        {day}
                      </div>;
                })}
                </div>
              </motion.div>
            </motion.div>

            <motion.div className="flex flex-col sm:flex-row items-stretch justify-center border border-sand-dark/30 rounded-2xl bg-white overflow-hidden" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
            once: true,
            margin: '-80px'
          }} data-id="element-795">
              {[{
              value: '10',
              label: 'Savaičių',
              sub: 'Programos trukmė'
            }, {
              value: '30',
              label: 'Užsiėmimų',
              sub: 'Iš viso sesijų'
            }, {
              value: '90',
              label: 'Akademinių val.',
              sub: 'Po 2–2.5 val.'
            }].map((stat, i, arr) => <motion.div key={stat.value} variants={fadeInUp} className="flex-1 flex flex-col items-center text-center px-8 py-8 relative" data-id="element-796">
                  {i < arr.length - 1 && <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2" style={{
                width: 1,
                height: '55%',
                background: 'rgb(var(--sand-dark) / 0.4)'
              }} data-id="element-797" />}
                  <span className="font-bold text-gold-dark leading-none mb-2" style={{
                fontSize: 'clamp(2.5rem, 5vw, 3.5rem)'
              }} data-id="element-798">
                    {stat.value}
                  </span>
                  <p className="text-sm font-semibold text-mountain-darker uppercase tracking-wide mb-1" data-id="element-799">
                    {stat.label}
                  </p>
                  <p className="text-xs text-mountain-dark/50" data-id="element-800">{stat.sub}</p>
                </motion.div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="py-16 px-4 bg-sand-lightest" data-id="element-801">
        <motion.div className="relative max-w-6xl mx-auto min-h-[420px] rounded-3xl overflow-hidden flex items-center justify-center" {...fadeInUp} data-id="element-802">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url('${HERO_MOUNTAIN_IMAGE}')`
        }} data-id="element-803" />
          <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.78) 100%)'
        }} data-id="element-804" />
          <div className="relative z-10 text-center px-6 py-16 flex flex-col items-center" data-id="element-805">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight" data-id="element-806">
              Pradėk Savo Kelionę Šiandien
            </h2>
            <div className="w-16 h-px bg-gold mx-auto mb-4" data-id="element-807" />
            <p className="font-accent italic text-2xl md:text-3xl text-gold-light mb-10" data-id="element-808">
              Gyvenimas prasmingas tada, kai gyveni sąmoningai.
            </p>
            <motion.button whileHover={{
            backgroundColor: 'rgb(var(--gold))',
            color: 'rgb(var(--mountain-darker))',
            scale: 1.05,
            boxShadow: '0 20px 50px -10px rgba(0,0,0,0.5)'
          }} whileTap={{
            scale: 0.97
          }} className="bg-white text-mountain-darker px-12 py-5 rounded-full text-lg font-semibold shadow-2xl transition-colors inline-flex items-center gap-2" data-id="element-809">
              Rezervuok kursą
              <ArrowRightIcon className="w-5 h-5" data-id="element-810" />
            </motion.button>
          </div>
        </motion.div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-12 px-4 bg-sand-lightest border-t border-sand-dark/20" data-id="element-811">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-812">
          <div className="flex items-center gap-2" data-id="element-813">
            <span className="text-gold-dark font-bold text-base tracking-wide" data-id="element-814">
              DFB
            </span>
            <span className="text-mountain-dark/60 font-medium text-sm tracking-widest uppercase" data-id="element-815">
              Metodika
            </span>
          </div>
          <p className="text-mountain-dark/50 text-sm" data-id="element-816">
            © 2026 DFB Metodika. Visos teisės saugomos.
          </p>
          <div className="flex items-center gap-6" data-id="element-817">
            <a href="#apie" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-818">
              Apie
            </a>
            <a href="#metodika" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-819">
              Metodika
            </a>
            <a href="#registracija" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-820">
              Registracija
            </a>
          </div>
        </div>
      </footer>
    </main>;
}